import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import firebase from 'firebase/app';
import messaging from '../init-fcm';
import { saveNotificationToken } from '../services/notification/actions';
import {
    initNativePush,
    isNativePush,
    pushTokenMeta,
    setPendingPushPath,
    takePendingPushPath,
} from '../helpers/nativePush';

// Simulated token for development environment
const DEVELOPMENT_TOKEN = "dev-fcm-token-for-testing-purposes-only";
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

/** iOS Safari and some WebKit contexts omit `window.Notification`; reading `Notification` throws ReferenceError. */
function hasWebNotificationApi() {
    return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * Same-origin favicon for notification icon/badge.
 * Cross-origin absolute URLs (API host vs PWA host) are often dropped by Chrome.
 */
function resolveNotificationAssetUrl(urlFromPayload, size = 'icon') {
    const hostingerPath =
        size === 'badge'
            ? '/public/assets/img/favicons/favicon-96x96.png'
            : '/public/assets/img/favicons/favicon-512x512.png';
    const craPath =
        size === 'badge'
            ? '/assets/img/favicons/favicon-96x96.png'
            : '/assets/img/favicons/favicon-512x512.png';
    const isHostinger = (window.location.hostname || '').indexOf('hostinger') !== -1;
    const defaultPath = isHostinger ? hostingerPath : craPath;

    if (!urlFromPayload) {
        return defaultPath;
    }

    try {
        const resolved = new URL(urlFromPayload, window.location.origin);
        if (resolved.origin !== window.location.origin) {
            return defaultPath;
        }
        const pathname = resolved.pathname || '';
        if (
            isHostinger &&
            pathname.indexOf('/assets/img/favicons/') === 0 &&
            pathname.indexOf('/public/') !== 0
        ) {
            return '/public' + pathname;
        }
        return pathname || defaultPath;
    } catch (e) {
        return defaultPath;
    }
}

function getWebNotificationPermission() {
    return hasWebNotificationApi() ? Notification.permission : 'unsupported';
}

/** Register Firebase SW only when the script is real JS (not SPA HTML fallback). */
async function registerFirebaseMessagingServiceWorker() {
    const response = await fetch('/firebase-messaging-sw.js', {
        headers: { 'Service-Worker': 'script' },
        cache: 'no-cache',
    });
    const contentType = response.headers.get('content-type') || '';
    const isJs =
        contentType.indexOf('javascript') !== -1 ||
        contentType.indexOf('ecmascript') !== -1;
    if (!response.ok || !isJs) {
        throw new Error(
            `firebase-messaging-sw.js unavailable or wrong MIME (${contentType || 'unknown'})`
        );
    }
    return navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
    });
}

class PushNotificationManager extends Component {
    componentDidMount() {
        this.initializePushNotifications();
        this.bindServiceWorkerNavigation();
        this.bindNativeTapListeners();
    }

    componentWillUnmount() {
        this.unbindServiceWorkerNavigation();
    }

    /**
     * When a push notification is clicked while the PWA is already open,
     * the service worker posts NOTIFICATION_NAVIGATE so we can deep-link
     * (e.g. /my-orders?order=XYZ) without a full reload.
     */
    bindServiceWorkerNavigation = () => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
            return;
        }
        this.handleServiceWorkerMessage = (event) => {
            const data = event && event.data;
            if (!data || data.type !== 'NOTIFICATION_NAVIGATE' || !data.url) {
                return;
            }

            let path = String(data.url);
            try {
                // Absolute URLs → path + search only
                if (/^https?:\/\//i.test(path)) {
                    const parsed = new URL(path);
                    path = parsed.pathname + parsed.search + parsed.hash;
                }
                // Strip Laravel /public prefix from marketing storeUrl links
                if (path === '/public' || path.indexOf('/public?') === 0) {
                    path = '/' + path.slice('/public'.length);
                } else if (path.indexOf('/public/') === 0) {
                    path = path.slice('/public'.length) || '/';
                }
                if (
                    path === '/my-favorite-stores' ||
                    path.indexOf('/my-favorite-stores?') === 0 ||
                    path === '/my-favorite' ||
                    path.indexOf('/my-favorite?') === 0
                ) {
                    path = '/my-wishlist?tab=stores';
                }
            } catch (e) {
                return;
            }

            if (!path.startsWith('/')) {
                return;
            }

            const { history, location } = this.props;
            if (!history) {
                window.location.assign(path);
                return;
            }

            const current = (location && (location.pathname + location.search + (location.hash || ''))) || '';
            if (current === path) {
                // Force Orders to re-sync focus when already on the same deep-link.
                history.replace(path);
                return;
            }
            history.push(path);
        };
        navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage);
    };

    unbindServiceWorkerNavigation = () => {
        if (
            typeof navigator === 'undefined' ||
            !('serviceWorker' in navigator) ||
            !this.handleServiceWorkerMessage
        ) {
            return;
        }
        navigator.serviceWorker.removeEventListener('message', this.handleServiceWorkerMessage);
    };

    /** In-app navigation for a tapped push (native). Logged-out taps wait until login. */
    navigateToPushPath = (path) => {
        const { history, location, user } = this.props;
        if (!path || !history) return;
        if (!(user && user.success)) {
            setPendingPushPath(path);
            return;
        }
        const current = (location && (location.pathname + location.search + (location.hash || ''))) || '';
        if (current === path) {
            history.replace(path);
        } else {
            history.push(path);
        }
    };

    /** Native shells: bind tap listeners immediately (cold start) but only ask for permission after login. */
    bindNativeTapListeners = () => {
        if (!isNativePush()) return;
        initNativePush({
            register: false,
            onNavigate: (path) => this.navigateToPushPath(path),
        });
    };

    initializeNativePush = () => {
        initNativePush({
            onToken: (token) => {
                const { user } = this.props;
                if (user && user.data && user.data.id && user.data.auth_token) {
                    this.props.saveNotificationToken(token, user.data.id, user.data.auth_token, pushTokenMeta('customer'));
                }
            },
            onNavigate: (path) => this.navigateToPushPath(path),
        });
    };

    initializePushNotifications = () => {
        const { user, settings } = this.props;

        // Native app: use the real FCM/APNs token so pushes reach the OS notification panel.
        if (isNativePush()) {
            if (user && user.success) {
                this.initializeNativePush();
            }
            return;
        }
        
        // Check settings from Redux state (preferred) or localStorage (fallback)
        const enablePushNotification = (settings && settings.enablePushNotification === "true") || 
                                       localStorage.getItem("enablePushNotification") === "true";
        
        // Debug logging
        console.log('[PushNotificationManager] Initializing', {
            user_logged_in: !!(user && user.success),
            settings_from_redux: settings && settings.enablePushNotification,
            localStorage_flag: localStorage.getItem("enablePushNotification"),
            firebase_supported: firebase.messaging.isSupported(),
            is_development: isDevelopment,
            push_enabled: enablePushNotification
        });
        
        // Always try to initialize if user is logged in AND push notifications are enabled
        if (user && user.success && enablePushNotification) {
            if (firebase.messaging.isSupported()) {
                // Always try to get real FCM token first, even in development
                this.registerForPushNotifications();
            } else if (isDevelopment) {
                // Firebase not supported, use development token only in dev
                this.handleDevelopmentToken();
            }
        } else if (user && user.success && hasWebNotificationApi() && Notification.permission === 'granted') {
            // Fallback: If permission is already granted but settings flag not set,
            // still try to get token (user might have enabled manually)
            console.log('[PushNotificationManager] Permission granted but settings flag not set, attempting token registration anyway');
            if (firebase.messaging.isSupported()) {
                this.registerForPushNotifications();
            }
        } else {
            // Log why initialization was skipped
            console.log('[PushNotificationManager] Initialization skipped', {
                user_logged_in: !!(user && user.success),
                push_enabled: enablePushNotification,
                firebase_supported: firebase.messaging.isSupported(),
                is_development: isDevelopment,
                notification_permission: getWebNotificationPermission()
            });
        }
    }

    // Development environment workaround
    handleDevelopmentToken = async () => {
        if (!hasWebNotificationApi()) return;
        try {
            const permission = await Notification.requestPermission();
            
            if (permission === "granted") {
                // Save the development token to the backend
                const { user } = this.props;
                if (user && user.data && user.data.id && user.data.auth_token) {
                    this.props.saveNotificationToken(DEVELOPMENT_TOKEN, user.data.id, user.data.auth_token);
                }
            }
        } catch (error) {
            // Silent error handling in production
        }
    }
    
    showDevelopmentNotification = () => {
        if ('Notification' in window && Notification.permission === 'granted') {
            try {
                if ('serviceWorker' in navigator) {
                    navigator.serviceWorker.ready.then(registration => {
                        registration.showNotification('Development Mode', {
                            body: 'This is a test notification in development mode.',
                            icon: '/public/assets/img/favicons/favicon-96x96.png',
                            vibrate: [100, 50, 100],
                            requireInteraction: true
                        });
                    });
                }
            } catch (error) {
                // Silent error handling in production
            }
        }
    }

    registerForPushNotifications = async () => {
        const { user } = this.props;
        
        try {
            // Check if firebase messaging is properly initialized
            if (!messaging) {
                console.warn('[PushNotificationManager] Firebase messaging not initialized');
                return;
            }

            if (!hasWebNotificationApi()) {
                console.warn('[PushNotificationManager] Web Notifications API not available in this browser');
                return;
            }
            
            // Ensure service worker is ready before requesting permission (critical for iOS)
            if ('serviceWorker' in navigator) {
                try {
                    await navigator.serviceWorker.ready;
                } catch (swError) {
                    console.warn('[PushNotificationManager] Service worker not ready', swError);
                    // Continue anyway, as some browsers may still work
                }
            }
            
            // Check current permission status
            if (Notification.permission === 'granted') {
                // Permission already granted (manually or previously), proceed to get token
                console.log('[PushNotificationManager] Permission already granted, getting FCM token');
                await this.getFCMTokenAndSetup();
            } else if (Notification.permission === 'default') {
                // Permission not yet requested - request it
                // Note: On iOS, this must be triggered by user interaction
                console.log('[PushNotificationManager] Requesting notification permission');
                const permission = await Notification.requestPermission();
                
                console.log('[PushNotificationManager] Permission result:', permission);
                
                if (permission === "granted") {
                    await this.getFCMTokenAndSetup();
                } else {
                    console.log('[PushNotificationManager] Notification permission denied:', permission);
                }
            } else {
                // Permission denied
                console.log('[PushNotificationManager] Notification permission was previously denied');
                console.log('[PushNotificationManager] User needs to enable notifications in browser settings');
            }
        } catch (error) {
            console.error('[PushNotificationManager] Error registering for push notifications:', error);
        }
    }
    
    getFCMTokenAndSetup = async () => {
        const { user } = this.props;
        
        console.log('[PushNotificationManager] getFCMTokenAndSetup called', {
            user_logged_in: !!(user && user.success),
            user_id: user && user.data ? user.data.id : null,
            has_auth_token: !!(user && user.data && user.data.auth_token),
            notification_permission: getWebNotificationPermission()
        });
        
        try {
            // Get the specific Firebase messaging service worker registration
            let registration = null;
            if ('serviceWorker' in navigator) {
                try {
                    // Get all service worker registrations
                    const registrations = await navigator.serviceWorker.getRegistrations();
                    
                    console.log('[PushNotificationManager] Found service worker registrations:', registrations.map(reg => {
                        const scriptURL = (reg.active && reg.active.scriptURL) || 
                                        (reg.installing && reg.installing.scriptURL) || 
                                        (reg.waiting && reg.waiting.scriptURL) || 
                                        'N/A';
                        return { scope: reg.scope, scriptURL: scriptURL };
                    }));
                    
                    // Find the Firebase messaging service worker registration
                    registration = registrations.find(reg => {
                        const scriptURL = (reg.active && reg.active.scriptURL) || 
                                        (reg.installing && reg.installing.scriptURL) || 
                                        (reg.waiting && reg.waiting.scriptURL) || 
                                        '';
                        const isFirebaseSW = scriptURL.includes('firebase-messaging-sw.js');
                        console.log('[PushNotificationManager] Checking registration:', {
                            scope: reg.scope,
                            scriptURL: scriptURL,
                            isFirebaseSW: isFirebaseSW
                        });
                        return reg.scope === '/' && isFirebaseSW;
                    });
                    
                    // If not found, unregister conflicting service workers and register Firebase one
                    if (!registration) {
                        console.log('[PushNotificationManager] Firebase messaging SW not found, unregistering conflicting SWs...');
                        
                        // Unregister all non-Firebase service workers with same scope
                        for (const reg of registrations) {
                            const scriptURL = (reg.active && reg.active.scriptURL) || 
                                            (reg.installing && reg.installing.scriptURL) || 
                                            (reg.waiting && reg.waiting.scriptURL) || 
                                            '';
                            if (reg.scope === '/' && !scriptURL.includes('firebase-messaging-sw.js')) {
                                console.log('[PushNotificationManager] Unregistering conflicting SW:', scriptURL);
                                await reg.unregister();
                            }
                        }
                        
                        console.log('[PushNotificationManager] Registering Firebase messaging service worker...');
                        registration = await registerFirebaseMessagingServiceWorker();
                        
                        // Wait for it to be activated
                        await new Promise((resolve, reject) => {
                            if (registration.installing) {
                                registration.installing.addEventListener('statechange', function() {
                                    if (this.state === 'activated') {
                                        resolve();
                                    } else if (this.state === 'redundant') {
                                        reject(new Error('Service worker became redundant'));
                                    }
                                });
                            } else if (registration.waiting) {
                                registration.waiting.addEventListener('statechange', function() {
                                    if (this.state === 'activated') {
                                        resolve();
                                    }
                                });
                                // Activate waiting service worker
                                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                            } else if (registration.active) {
                                resolve();
                            } else {
                                resolve();
                            }
                        });
                    }
                    
                    // Verify we have the correct registration
                    const finalScriptURL = (registration.active && registration.active.scriptURL) || 
                                         (registration.installing && registration.installing.scriptURL) || 
                                         (registration.waiting && registration.waiting.scriptURL) || 
                                         'N/A';
                    
                    if (!finalScriptURL.includes('firebase-messaging-sw.js')) {
                        console.error('[PushNotificationManager] Wrong service worker registered!', {
                            scriptURL: finalScriptURL,
                            expected: 'firebase-messaging-sw.js'
                        });
                        throw new Error('Wrong service worker registration');
                    }
                    
                    console.log('[PushNotificationManager] Firebase messaging service worker ready', {
                        scope: registration.scope,
                        scriptURL: finalScriptURL
                    });
                } catch (swError) {
                    console.error('[PushNotificationManager] Service worker error:', swError);
                    // Try to register Firebase messaging SW as fallback
                    try {
                        registration = await registerFirebaseMessagingServiceWorker();
                        // Wait a bit for it to activate
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    } catch (registerError) {
                        console.error('[PushNotificationManager] Failed to register Firebase messaging SW:', registerError);
                    }
                }
            }
            
            // Get FCM token
            const vapidKey = localStorage.getItem("firebasePublic") || "BJNELRbvvf5eWmfUUDM4dRA_-Hw6wQGJgeRz86xMFAGa5k-Rwwf5H6WeFJzGrNgz_nqo0kLS3ylJIoXxyrCk8cs";
            const vapidKeySource = localStorage.getItem("firebasePublic") ? 'localStorage' : 'fallback';
            console.log('[PushNotificationManager] Requesting FCM token', {
                has_vapid_key: !!vapidKey,
                vapid_key_preview: vapidKey.substring(0, 20) + '...',
                vapid_key_length: vapidKey.length,
                vapid_key_source: vapidKeySource,
                vapid_key_full: vapidKey, // Log full key for debugging
                has_registration: !!registration,
                registration_scope: registration ? registration.scope : 'N/A',
                is_localhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
                protocol: window.location.protocol
            });
            
            // Get FCM token (vapidKey already set via usePublicVapidKey() in init-fcm.js)
            const token = await messaging.getToken({
                serviceWorkerRegistration: registration || undefined
            });
            
            if (token) {
                console.log('[PushNotificationManager] FCM token obtained successfully', {
                    token_preview: token.substring(0, 20) + '...',
                    token_length: token.length,
                    user_id: user && user.data ? user.data.id : null,
                    has_auth_token: !!(user && user.data && user.data.auth_token)
                });
                
                // Save the token to backend
                if (user && user.data && user.data.id && user.data.auth_token) {
                    console.log('[PushNotificationManager] Saving token to backend...');
                    this.props.saveNotificationToken(token, user.data.id, user.data.auth_token);
                } else {
                    console.warn('[PushNotificationManager] Cannot save token - missing user data', {
                        has_user: !!user,
                        has_user_data: !!(user && user.data),
                        has_user_id: !!(user && user.data && user.data.id),
                        has_auth_token: !!(user && user.data && user.data.auth_token)
                    });
                }
                
                // Set up token refresh handler
                messaging.onTokenRefresh(async () => {
                    try {
                        // Get Firebase messaging service worker registration for refresh
                        let refreshRegistration = null;
                        if ('serviceWorker' in navigator) {
                            try {
                                const registrations = await navigator.serviceWorker.getRegistrations();
                                refreshRegistration = registrations.find(reg => {
                                    const scriptURL = (reg.active && reg.active.scriptURL) || 
                                                    (reg.installing && reg.installing.scriptURL) || 
                                                    (reg.waiting && reg.waiting.scriptURL) || 
                                                    '';
                                    return reg.scope === '/' && scriptURL.includes('firebase-messaging-sw.js');
                                });
                            } catch (e) {
                                console.warn('[PushNotificationManager] Error getting registration for token refresh:', e);
                            }
                        }
                        
                        // Get refreshed token (vapidKey already set via usePublicVapidKey() in init-fcm.js)
                        const refreshedToken = await messaging.getToken({
                            serviceWorkerRegistration: refreshRegistration || undefined
                        });
                        
                        if (refreshedToken && user && user.data && user.data.id && user.data.auth_token) {
                            console.log('[PushNotificationManager] Token refreshed');
                            this.props.saveNotificationToken(refreshedToken, user.data.id, user.data.auth_token);
                        }
                    } catch (error) {
                        console.error('[PushNotificationManager] Error refreshing token:', error);
                    }
                });
                
                // Handle foreground messages
                messaging.onMessage((payload) => {
                    console.log('[PushNotificationManager] Foreground message received:', payload);
                    
                    // Create a notification if the app is in the foreground
                    if (hasWebNotificationApi() && Notification.permission === "granted") {
                        const notificationTitle = (payload.data && payload.data.title) || (payload.notification && payload.notification.title) || 'New Notification';
                        const notificationBody = (payload.data && payload.data.message) || (payload.notification && payload.notification.body) || '';
                        const notificationOptions = {
                            body: notificationBody,
                            icon: resolveNotificationAssetUrl(
                                (payload.data && payload.data.icon) ||
                                    (payload.notification && payload.notification.icon),
                                'icon'
                            ),
                            badge: resolveNotificationAssetUrl(
                                (payload.data && payload.data.badge) ||
                                    (payload.notification && payload.notification.badge),
                                'badge'
                            ),
                            data: {
                                url: (payload.data && payload.data.click_action) || '/',
                            },
                        };
                        
                        // Add image if available (admin-uploaded large picture)
                        const imageUrl =
                            (payload.data && payload.data.image) ||
                            (payload.data && payload.data.custom_image) ||
                            (payload.notification && payload.notification.image);
                        if (imageUrl) {
                            notificationOptions.image = imageUrl;
                        }
                        
                        // Play notification sound
                        try {
                            const audio = new Audio("/assets/audio/customer-notification.mp3");
                            audio.play().catch(e => console.warn('Could not play notification sound:', e));
                        } catch (audioError) {
                            // Audio playback failed, continue without sound
                        }
                        
                        // Vibrate if supported
                        if ("vibrate" in navigator) {
                            navigator.vibrate(100);
                        }
                        
                        // Show notification via service worker
                        if ('serviceWorker' in navigator) {
                            navigator.serviceWorker.ready.then(reg => {
                                reg.showNotification(notificationTitle, notificationOptions);
                            });
                        }
                    }
                });
            } else {
                console.warn('[PushNotificationManager] No FCM token received');
            }
        } catch (tokenError) {
            console.error('[PushNotificationManager] Error getting FCM token:', tokenError);
            console.error('[PushNotificationManager] Error details:', {
                error_code: tokenError.code,
                error_message: tokenError.message,
                error_stack: tokenError.stack,
                is_localhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
                protocol: window.location.protocol,
                note: 'FCM requires HTTPS in production. Localhost should work but may have limitations.'
            });
        }
    }

    componentDidUpdate(prevProps) {
        const prevUserSuccess = prevProps && prevProps.user && prevProps.user.success;
        const currentUserSuccess = this.props.user && this.props.user.success;
        const prevSettings = prevProps && prevProps.settings;
        const currentSettings = this.props.settings;
        
        // If user just logged in, try to initialize
        if (!prevUserSuccess && currentUserSuccess) {
            this.initializePushNotifications();
            // Replay a notification tap that arrived while logged out.
            const pending = takePendingPushPath();
            if (pending && this.props.history) {
                this.props.history.push(pending);
            }
        }
        
        // If settings just loaded, try to initialize
        if ((!prevSettings || !prevSettings.enablePushNotification) && 
            currentSettings && currentSettings.enablePushNotification === "true") {
            console.log('[PushNotificationManager] Settings loaded, initializing push notifications');
            this.initializePushNotifications();
        }
    }

    render() {
        return null; // This component doesn't render anything
    }
}

const mapStateToProps = (state) => ({
    user: state.user.user,
    settings: state.settings.settings  // Add settings to props
});

export default withRouter(
    connect(mapStateToProps, { saveNotificationToken })(PushNotificationManager)
); 